import type { SxProps, Theme } from '@mui/material/styles';
import { MenuItem, Select } from '@shared/ui';
import type React from 'react';

const TIME_OPTION_STEP_MINUTES = 15;
const TIME_OPTIONS = buildTimeOptions();

interface TimeSelectProps {
  label: string;
  value: string;
  disabled?: boolean;
  variant?: 'standard' | 'outlined';
  /**
   * Render a native `select`. Preferred inside dialogs: it avoids stacking a
   * second focus-trapping popover on top of the dialog, and gives mobile users
   * the platform picker.
   */
  native?: boolean;
  sx?: SxProps<Theme>;
  onChange: (value: string) => void;
}

/**
 * Quarter-hour time picker shared by availability windows and out-of-office blocks.
 *
 * Deliberately not a native time input: Chrome ignores `step` when building the
 * dropdown list for `time`/`datetime-local`, so the only way to actually restrict
 * the offered minutes to :00/:15/:30/:45 is to render the options ourselves.
 */
const TimeSelect: React.FC<TimeSelectProps> = ({
  label,
  value,
  disabled = false,
  variant = 'standard',
  native = false,
  sx = { flex: '0 0 72px' },
  onChange,
}) => (
  <Select
    value={value}
    disabled={disabled}
    size="small"
    variant={variant}
    native={native}
    inputProps={{ 'aria-label': label }}
    renderValue={native ? undefined : (selected) => formatTimeLabel(String(selected))}
    onChange={(event) => onChange(String(event.target.value))}
    sx={sx}
  >
    {native
      ? NATIVE_TIME_OPTIONS
      : TIME_OPTIONS.map((option) => (
          <MenuItem key={option} value={option}>
            {formatTimeLabel(option)}
          </MenuItem>
        ))}
  </Select>
);

// Static across every instance and every render: 96 options rebuilt on each
// keystroke measurably slows the out-of-office dialog (and its test suite).
const NATIVE_TIME_OPTIONS = TIME_OPTIONS.map((option) => (
  <option key={option} value={option}>
    {formatTimeLabel(option)}
  </option>
));

function buildTimeOptions(): string[] {
  const options: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += TIME_OPTION_STEP_MINUTES) {
    options.push(formatHHMM(minutes));
  }
  return options;
}

function formatHHMM(totalMinutes: number): string {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

function formatTimeLabel(value: string): string {
  if (value === '24:00') {
    return '12:00am';
  }
  const [hourPart, minutePart] = value.split(':');
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return value;
  }
  const period = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.toString().padStart(2, '0')}${period}`;
}

export default TimeSelect;
